<template>
    <DCard padding>
        <DRow align-vertical="end">
            <DCol
                sm="12"
                class="text-right"
            >
                <div class="list-configuration">
                    <p>{{ __('products.products.index.list_configuration_hint') }}</p>
                    <DButton
                        id="table-configuration"
                        icon="cog-outline"
                        :label="__('messages.list_configuration')"
                        class="float-end"
                        @click="showListViewModal = true"
                    />
                </div>
            </DCol>
        </DRow>
        <DRow
            class="mt-2 mb-2"
            align-vertical="end"
        >
            <DCol sm="2">
                <DInput
                    id="search"
                    v-model="filter.search"
                    :label="__('messages.search')"
                    :placeholder="__('products.products.filter.search')"
                    @keydown.enter.exact.prevent="submitFilters(products.current_page)"
                />
            </DCol>
            <DCol sm="2">
                <DMultiSelect
                    id="ff-modules-dropdown"
                    :options="moduleOptions"
                    :label="__('products.products.filter.fulfillment_modules')"
                    :search="true"
                    :selection="true"
                    :selected="selectedModules"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    selection-type="text"
                    @update="updateSelectedModules"
                />
            </DCol>
            <DCol sm="2">
                <DMultiSelect
                    id="brands-dropdown"
                    :options="brandOptions"
                    :label="__('products.products.filter.brands')"
                    :search="true"
                    :selection="true"
                    :selected="selectedBrands"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    selection-type="text"
                    @update="updateSelectedBrands"
                />
            </DCol>
            <DCol sm="2">
                <DMultiSelect
                    id="classes-dropdown"
                    :options="classOptions"
                    :label="__('products.products.index.table.headers.classes')"
                    :search="true"
                    :selection="true"
                    :selected="selectedClass"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    selection-type="text"
                    @update="updateSelectedClasses"
                />
            </DCol>
            <DCol sm="2">
                <DMultiSelect
                    id="classes-dropdown"
                    :options="groupOptions"
                    :label="__('products.products.filter.product_group')"
                    :search="true"
                    :selection="true"
                    :selected="selectedGroup"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    selection-type="text"
                    @update="updateSelectedGroup"
                />
            </DCol>
            <DCol sm="2">
                <h6>
                    {{ __('products.products.filter.retirement_status') }}
                </h6>
                <DSwitch
                    id="retirement"
                    style="margin-left: 10px;"
                    color="success"
                    variant="3d"
                    :checked="shouldIncludeRetired"
                    @update:checked="updateRetired"
                />
            </DCol>
        </DRow>
        <DRow
            class="mt-2 mb-2"
            align-vertical="end"
        >
            <DCol sm="3">
                <DButton
                    id="filter-apply"
                    :label="__('messages.actions.filter')"
                    @click="submitFilters(products.current_page)"
                />
                <DButton
                    id="filter-reset"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <DTable
            :items="products.items"
            :fields="getFields"
            :sorter="isSortable"
            :sorter-value="sorterValue"
            class="table-responsive"
            @update:sorter-value="updateOrderValue"
        >
            <template #name="{item}">
                <td>
                    {{ item.name }}
                    <DBadge
                        v-if="item.retired || item.product_group_retired"
                        color="info"
                    >
                        {{ __('products.products.index.retired') }}
                    </DBadge>
                </td>
            </template>
            <template #automatic_price_adjustment="{item}">
                <td>
                    <DTag
                        v-if="item.automatic_price_adjustment === true"
                        v-c-tooltip="item.maximum_annual_price"
                        color="success"
                        :label="__('products.products.index.active')"
                    />
                    <DTag
                        v-else
                        color="danger"
                        :label="__('products.products.index.inactive')"
                    />
                </td>
            </template>
            <template #winback_ticket_on_termination="{item}">
                <td>
                    <DTag
                        v-if="item.winback_ticket_on_termination === true"
                        color="success"
                        :label="__('products.products.index.yes')"
                    />
                    <DTag
                        v-else
                        color="danger"
                        :label="__('products.products.index.no')"
                    />
                </td>
            </template>
            <template #fulfillment_strategy="{item}">
                <td>
                    {{ __(`products.fulfillmentStrategies.values.${item.fulfillment_strategy}`) }}
                </td>
            </template>
            <template #invoice_behaviour="{item}">
                <td>
                    {{ __(`products.invoice_behaviours.${item.invoice_behaviour}`) }}
                </td>
            </template>
            <template #possible_upgrades="{item}">
                <td>
                    <DTag
                        v-if="item.possible_upgrades.length > 0"
                        v-c-tooltip="item.possible_upgrades"
                        color="success"
                        :label="__('products.products.index.yes')"
                    />
                    <DTag
                        v-else
                        color="danger"
                        :label="__('products.products.index.no')"
                    />
                </td>
            </template>
            <template #possible_add_on_groups="{item}">
                <td>
                    <DTag
                        v-if="item.possible_add_on_groups.length > 0"
                        v-c-tooltip="item.possible_add_on_groups"
                        color="success"
                        :label="__('products.products.index.yes')"
                    />
                    <DTag
                        v-else
                        color="danger"
                        :label="__('products.products.index.no')"
                    />
                </td>
            </template>
            <template #termination_on_dunning="{item}">
                <td>
                    <DTag
                        v-if="item.termination_on_dunning === true"
                        color="success"
                        :label="__('products.products.index.yes')"
                    />
                    <DTag
                        v-else
                        color="danger"
                        :label="__('products.products.index.no')"
                    />
                </td>
            </template>
            <template #price_default_plan_price="{item}">
                <td>
                    <Link
                        v-if="item.price_default_plan_price"
                        :href="viewProductLink(item.id)"
                    >
                        {{ item.price_default_plan_price }}
                    </Link>
                </td>
            </template>
            <template #actions="{item}">
                <td>
                    <div class="actions-btn">
                        <DButton
                            v-if="userPermissions.includes('product.view')"
                            :id="`edit_product_${item.id}`"
                            icon="eye"
                            mini
                            :href="viewProductLink(item.id)"
                        />
                        <DButton
                            v-if="userPermissions.includes('product.edit')"
                            :id="`edit_product_${item.id}`"
                            icon="pencil"
                            mini
                            :href="editProductLink(item.id)"
                        />
                        <DButton
                            v-if="userPermissions.includes('product.delete')"
                            :id="`delete_product_${item.id}`"
                            :disabled="item.has_contracts === true || isInLoadingState"
                            icon="trash-can-outline"
                            color="danger"
                            mini
                            @click="openDeleteProductModal(item.id)"
                        />
                    </div>
                </td>
            </template>
        </DTable>
        <DPagination
            v-if="products.last_page > 1"
            :active-page="products.current_page"
            :pages="products.last_page"
            align="center"
            @update:activePage="submitFilters"
        />

        <DModal
            v-model="activityLogComment"
            :show="showDeleteProductConfirmationModal || modalHasErrors"
            :title="__('products.products.index.deleteProductConfirmation.title')"
            :message="__('products.products.index.deleteProductConfirmation.message')"
            is-confirm
            is-error
            :is-awaiting-validation="true"
            :show-change-reason-input="true"
            @close="showDeleteProductConfirmationModal = false"
            @confirm="deleteProduct()"
        />
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
    </DCard>
</template>

<script setup>
import { router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { computed, ref } from 'vue'
import { __ } from '~@/Composables/translation'
import ListViewModal from '~@/Components/ListView/ListViewModal'

const props = defineProps({
    products: {
        type: Array,
        default () {
            return []
        }
    },
    brands: {
        type: Array,
        default () {
            return []
        }
    },
    modules: {
        type: Array,
        default () {
            return []
        }
    },
    classes: {
        type: Array,
        default () {
            return []
        }
    },
    filterData: {
        type: Object,
        default () {
            return {}
        }
    },
    groups: {
        type: Array,
        default: () => { return [] }
    },
    userListView: {
        type: Array,
        default: () => {
            return []
        }
    }
})

const filter = {
    search: props.filterData.search ?? null,
    brands: props.filterData.brands ?? [],
    modules: props.filterData.modules ?? [],
    retired: props.filterData.retired ?? false,
    classes: props.filterData.classes ?? [],
    groups: props.filterData.groups ?? [],
    orderBy: props.filterData.orderBy ?? 'id',
    orderByAsc: props.filterData.orderByAsc ?? false
}

const moduleOptions = computed(function () {
    return props.modules.map((value) => {
        return { value, text: value, selected: props.filterData?.modules?.includes(String(value)) }
    })
})

const brandOptions = computed(function () {
    return props.brands.map((item) => {
        return { value: item.value, text: item.label, selected: props.filterData?.brands?.includes(String(item.value)) }
    })
})

const classOptions = computed(function () {
    return props.classes.map((item) => {
        return { value: item.value, text: item.label, selected: props.filterData?.classes?.includes(String(item.value)) }
    })
})

const groupOptions = computed(function () {
    return props.groups.map((item) => {
        return { value: item.value, text: item.label, selected: props.filterData?.groups?.includes(String(item.value)) }
    })
})

const selectedModules = computed(function () {
    if (!filter.modules) return []

    return filter.modules.map((item) => item)
})

const selectedBrands = computed(function () {
    if (!filter.brands) return []

    return filter.brands.map((item) => parseInt(item))
})

const selectedClass = computed(function () {
    if (!filter.classes) return []

    return filter.classes.map((item) => parseInt(item))
})

const selectedGroup = computed(function () {
    if (!filter.groups) return []

    return filter.groups.map((item) => parseInt(item))
})

const shouldIncludeRetired = computed(function () {
    return Number(filter.retired) !== 0
})

const userPermissions = computed(() => usePage().props.auth.permissions)
const showListViewModal = ref(false)
const showDeleteProductConfirmationModal = ref(false)
const modalHasErrors = ref(false)
const activityLogComment = ref(null)
const productId = ref(null)
const getFields = computed(() => {
    const availableList = []

    props.userListView.forEach((item) => {
        if (item.show_column === true) {
            availableList.push({
                key: item.column,
                label: item.column !== 'actions' ? __('settings.list_view.' + item.list + '.' + item.column) : '',
                sorter: item.sort_column_enabled,
                _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
            })
        }
    })

    return availableList
})

const isSortable = computed(() => {
    if (!sorterValue.value.column) return false

    return { external: true }
})

const sorterValue = computed(() => {
    return {
        column: filter.orderBy,
        asc: !!filter.orderByAsc
    }
})
const updateOrderValue = (event) => {
    filter.orderBy = event.column
    filter.orderByAsc = event.asc ? 1 : 0

    submitFilters(props.products.current_page)
}

function viewProductLink (id) {
    return route('products.products.show', id)
}

function editProductLink (id) {
    return route('products.products.edit', id)
}

function updateSelectedModules (values) {
    filter.modules = values
}

function updateSelectedBrands (values) {
    filter.brands = values
}

function updateSelectedClasses (values) {
    filter.classes = values
}

function updateSelectedGroup (values) {
    filter.groups = values
}
function updateRetired (values) {
    filter.retired = values ? 1 : 0
}
function submitFilters (page) {
    const query = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v))

    if (page > 1) {
        query.page = page
    }

    router.get(route('products.products.advanced-view', {
        _query: query,
        saveFilters: true
    }))
}
function resetFilters () {
    router.get(route('products.products.advanced-view', {
        saveFilters: true,
        defaultFilters: true
    }))
}

function openDeleteProductModal (id) {
    productId.value = id
    showDeleteProductConfirmationModal.value = true
}

function deleteProduct () {
    router.delete(route('products.products.destroy', {
        product: productId.value,
        activity_log_comment: activityLogComment.value
    }), {
        onSuccess: () => {
            showDeleteProductConfirmationModal.value = false
            modalHasErrors.value = false
        },
        onError: () => {
            modalHasErrors.value = true
        }
    })
}

</script>

<style lang="scss" scoped>
.list-configuration {
    display: flex;
    grid-gap: 10px;
    float: right;

    & > p {
        padding-top: 10px !important;
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
    }
}
.actions-btn {
    display: flex;
    grid-gap: 10px;
    float: right;
}
</style>
